import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, catchError, map, of, tap } from 'rxjs';
import { AppConstants } from '../commons/app-constants';
import { UserService } from './user.service';
import { Router } from '@angular/router';
import { CurrentUser } from '../interfaces/current-user';
import { isIosNative } from '../commons/utils/platform';
import { readRefCode, readRefToken } from '../commons/utils/affiliate-ref';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly logger = inject(NGXLogger);

  apiUrl = AppConstants.API_URL;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private router: Router,
  ) { }

  login(credentials: any): Observable<any> {
    return this.http.post(
      AppConstants.AUTH_API + 'signin',
      {
        email: credentials.username,
        password: credentials.password,
      },
      httpOptions
    );
  }

  register(user: any): Observable<any> {
    return this.http.post(
      AppConstants.AUTH_API + 'signup',
      {
        name: user.name,
        email: user.email,
        password: user.password,
        matchingPassword: user.matchingPassword,
        socialProvider: 'LOCAL',
      },
      httpOptions
    );
  }


  isUserLoggedIn(): Observable<boolean> {

    const isUserLoggedInUrl = `${AppConstants.API_URL}openforall/api/v1/login/is-user-loggedin`;
    // console.log(`isUserLoggedInUrl is ${isUserLoggedInUrl}`);
    this.callAppComponentInit();
    return this.http.get<boolean>(isUserLoggedInUrl);
  }

  getSecretKey(): Observable<string> {
    return this.http.get<string>(`${AppConstants.API_URL}api/v1/login/secret-key`);
  }

  getUserDetails(): Observable<string> {
    return this.http.get<string>(`${AppConstants.API_URL}api/v1/login/user-details`);
  }

  private invokeInitSource = new Subject<void>();
  invokeInit$ = this.invokeInitSource.asObservable();

  callAppComponentInit() {
    // this.invokeInitSource.next();
  }

  checkEmailExists(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}api/v1/login/check-email`, { params: { email } });
  }

  checkEmailVerified(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}api/v1/login/check-verified`, { params: { email } });
  }

  preRegister(user: any): Observable<any> {
    const url = `${this.apiUrl}api/v1/auth/pre-register`;
    const requestBody = {
      name: user.name,
      email: user.email,
      password: user.password,
      token: user.token,
      invitedUser: user.invitedUser,
      turnstileToken: user.turnstileToken ?? null,
      refClickToken: readRefToken(),
      refCode: readRefCode(),
      // The ticked "I agree" box: the server records every published legal document at sign-up (it was dropped here before, so a
      // brand-new user was asked to accept the documents again at first login).
      termsAccepted: user.termsAccepted === true ? true : null,
    };

    // Sent only on iOS — lets the verification email include an in-app deep link. Not
    // security-sensitive (see backend RegistrationController's own comment).
    const headers = isIosNative()
      ? httpOptions.headers.set('X-Client-Platform', 'ios')
      : httpOptions.headers;
    return this.http.post<any>(url, requestBody, { headers });
  }

  // Affiliate Partner Program (Phase 3) — set by AffiliateRedirectComponent (/r/:affiliateCode).
  // The read logic moved to commons/utils/affiliate-ref 2026-09-22 (same cookie-first-then-
  // localStorage behaviour as before for the token, plus the attribution-window expiry the bare
  // code never had), so this service, the login page's social-login URLs and the redirect
  // component all share one implementation instead of three copies. Neither value ever blocks
  // signup on the backend if resolution fails.

  customLogin(user: any): Observable<any> {
    this.logger.info(`CustomLogin ${JSON.stringify(user)}`)
    const url = `${this.apiUrl}api/v1/login`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'deviceId': user.deviceId ?? '',
        // 2026-09-16: lets TokenService.safeDeviceDetails() give the "you were signed out
        // elsewhere" notification a real device label on iOS. Capacitor's WKWebView User-Agent
        // isn't recognised by the backend's generic UA parser (ua-parser-java) — it was misreading
        // this app's own build number out of the string and showing "App 47 on iOS" instead of
        // a real device name. Same header already used for pre-register on iOS.
        ...(isIosNative() ? { 'X-Client-Platform': 'ios' } : {}),
      }),
    };
    const requestBody = {
      email: user.email,
      password: user.password,
      deviceId: user.deviceId,
      turnstileToken: user.turnstileToken ?? null,
    };

    return this.http.post<any>(
      url, requestBody, httpOptions);
  }

  refreshSession(): Observable<any> {
    const url = `${AppConstants.AUTH_API}refresh`;
    this.logger.debug('[AuthService] Refresh session request started');
    return this.http.post<any>(url, null, { withCredentials: true }).pipe(
      tap(() => this.logger.debug('[AuthService] Refresh session request succeeded')),
      catchError((error) => {
        this.logger.debug('[AuthService] Refresh session request failed', error);
        throw error;
      })
    );
  }

  // http://localhost:8080/api/v1/verify-email?token=306e4dbf-d5c3-433c-8c9c-d833fd59144f

  // POST, not GET — a plain GET must never mutate state (confirmed live: an email safety-scanner
  // silently pre-fetching the GET link burned the single-use token before the real user clicked).
  // Only fires when a person genuinely clicks the confirm button, never from a page just loading.
  verifyEmail(token: string): Observable<any> {
    const url = `${this.apiUrl}api/v1/verify-email`;
    return this.http.post<any>(url, { token });
  }

  resendVerificationEmail(email: string): Observable<any> {
    const url = `${this.apiUrl}api/v1/auth/resend-verification-email`;
    return this.http.post<any>(url, { email }, httpOptions);
  }
}
